import { SliderMarkLabelSlotProps } from '@mui/base'
import { FormControl, FormHelperText, Slider, SliderMarkLabel, Stack } from '@mui/material'
import { useCallback } from 'react'
import { form } from '../../../../formState'
import { IconButton } from './IconButton'
import { Rabbit } from './icons/Rabbit'
import { Tortoise } from './icons/Tortoise'
import { marks, snapToNext, snapToPrev } from './marks'

interface SliderFieldProps {
  fieldName: 'level' | 'firstTimeWindowValue' | 'secondTimeWindowValue'
  helperText?: string
}

type ClickableSliderMarkProps = SliderMarkLabelSlotProps & {
  onChange: (e: React.SyntheticEvent, value: number) => void
}

const ClickableSliderMarkLabel = (props: ClickableSliderMarkProps) => {
  const { onChange, ...restProps } = props

  const markValue = props.ownerState?.marks?.[props?.['data-index']]?.value

  const noop = useCallback((e: React.SyntheticEvent) => {
    e.stopPropagation()
  }, [])

  const onClick = useCallback(
    (e: React.SyntheticEvent) => {
      e.preventDefault()
      e.stopPropagation()

      if (typeof markValue === 'number') {
        onChange(e, markValue)
      }
    },
    [onChange, markValue],
  )

  return <SliderMarkLabel onMouseDown={noop} onTouchStart={noop} onClick={onClick} {...restProps} />
}

function valueLabelFormat(value: number) {
  return `${parseFloat((Math.abs(value) / 100).toFixed(2))}x`
}

export function DrivingTime(props: SliderFieldProps) {
  const { helperText, fieldName } = props

  const {
    field,
    formApi: { change },
  } = form.useFormField(fieldName)

  const onChangeBaseTrafficFactor = useCallback(
    (_: Event, val: number | number[]) => {
      const value = Array.isArray(val) ? val[0] : val
      change(fieldName, value)
    },
    [change],
  )

  function onSnapToPrev() {
    onChangeBaseTrafficFactor(new Event(''), snapToPrev(field.value ?? -50))
  }
  function onSnapToNext() {
    onChangeBaseTrafficFactor(new Event(''), snapToNext(field.value ?? -50))
  }

  return (
    <FormControl data-testid={`${fieldName}-field`}>
      <Stack gap={1}>
        <Stack direction="row" alignItems="flex-start" gap={1}>
          <IconButton onClick={onSnapToPrev} data-testid={`${fieldName}-prev-btn`}>
            <Rabbit />
          </IconButton>
          <Slider
            label={'texts.labelName'}
            size="small"
            min={-600}
            max={-50}
            step={1}
            name={fieldName}
            value={field.value}
            onChange={(_, value) => change(fieldName, Array.isArray(value) ? value[0] : value)}
            valueLabelFormat={valueLabelFormat}
            marks={marks}
            valueLabelDisplay="auto"
            components={{ MarkLabel: ClickableSliderMarkLabel }}
            // @ts-ignore
            componentsProps={{ markLabel: { onChange: onChangeBaseTrafficFactor } }}
          />
          <IconButton onClick={onSnapToNext} data-testid={`${fieldName}-next-btn`}>
            <Tortoise />
          </IconButton>
        </Stack>
        {helperText && <FormHelperText sx={{ marginX: 0 }}>{helperText}</FormHelperText>}
      </Stack>
    </FormControl>
  )
}
